import { JobApiService } from 'jobs/JobApiService';
import {
   CreateJobDto,
   CreateJobEmpty,
   CreateJobEmptyResponse,
   CreateJobResponse,
} from 'jobs/types';
import { autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { JobLangListViewModel } from 'jobs/job-form/job-lang-list';
import { SkillsViewModel } from 'jobs/models/skills.view-model';
import { JobLocationViewModel } from 'jobs/job-form/job-location';
import { SalaryGroupViewModel } from './job-salary-group';
import { JobCategoriesGroupStore } from './job-categories-group';
import { FormControl, FormGroup } from 'shared/libs/form';
import { Validators } from 'core/services/Validators';
import { AsyncState } from 'shared/libs/state';
import { JobViewStore } from './job-view-dialog';
import { ApiError } from 'shared/types';

export class JobFormManager {
   readonly jobAttributes = observable.box<CreateJobEmpty | null>(null);

   readonly locationVm = new JobLocationViewModel(this);
   readonly salaryGroupVm = new SalaryGroupViewModel(this);
   readonly jobAttributtesState = new AsyncState<CreateJobEmptyResponse>();
   readonly createJobState = new AsyncState<CreateJobResponse>();

   readonly form = new FormGroup(
      {
         planId: new FormControl<string>('', [Validators.required]),
         job_title: new FormControl<string>('', [Validators.required]),
         category: new FormControl<string>('', [Validators.required]),
         category_1: new FormControl<string>(''),
         category_2: new FormControl<string>(''),
         subCategory: new FormControl<number[]>([], [Validators.required], { unwrap: true }),
         subCategory_1: new FormControl<number[]>([], [], { unwrap: true }),
         subCategory_2: new FormControl<number[]>([], [], { unwrap: true }),
         work_type: new FormControl<string>('', [Validators.required]),
         experience: new FormControl<string>('', [Validators.required]),
         form_link: new FormControl<string>('', [Validators.urlFormat]),
         skill: new FormControl<number[]>([], [], { unwrap: true }),
         lng_1: new FormControl<string>(''),
         lng_2: new FormControl<string>(''),
         lng_skill_1: new FormControl<string>(''),
         lng_skill_2: new FormControl<string>(''),
         salary_max: new FormControl<string>('', [Validators.salaryNumeric]),
         salary_min: new FormControl<string>('', [Validators.salaryNumeric]),
         salary_currency: new FormControl<string>(''),
         only_resume: new FormControl<boolean>(true),
         questionary: new FormControl<boolean>(true),
         /**
          * @countryId 5 - Ukraine id default
          */
         country: new FormControl<string>('5', [Validators.required]),
         city: new FormControl<string>('', [Validators.required]),
         jobDescription: new FormControl<string>('', [Validators.required]),
      },
      [
         (controls) => {
            if (
               !controls['salary_currency'].value &&
               (controls['salary_min'].value || controls['salary_max'].value)
            ) {
               return { salary_currency: "Валюта обов'язкова" };
            }

            return {};
         },
      ]
   );

   readonly categoriesGroup: JobCategoriesGroupStore;
   readonly langGroupVm: JobLangListViewModel;
   readonly skillsVm: SkillsViewModel;
   readonly jobView: JobViewStore;

   constructor() {
      makeObservable(this, {
         experinceOptions: computed,
         worktypeOptions: computed,
      });
      this.categoriesGroup = new JobCategoriesGroupStore(this);
      this.langGroupVm = new JobLangListViewModel(this);

      this.skillsVm = new SkillsViewModel([], {
         onValueChange: (val) => {
            this.form.controls['skill'].setValue(val.map((skill) => skill.id));
         },
      });

      this.jobView = new JobViewStore(this);

      autorun(() => {
         console.log(this.form.value);
      });
   }

   get experinceOptions(): Array<{ id: string; label: string; value: string }> {
      const attr = this.jobAttributes.get();
      if (attr) {
         return Object.entries(attr.experience).map<{
            id: string;
            label: string;
            value: string;
         }>(([k, v]) => ({
            id: uuidv4(),
            value: k,
            label: v,
         }));
      }
      return [];
   }

   get worktypeOptions(): Array<{ id: string; label: string; value: string }> {
      const attr = this.jobAttributes.get();
      if (attr) {
         return Object.entries(attr.worktype).map<{
            id: string;
            label: string;
            value: string;
         }>(([k, v]) => ({
            id: uuidv4(),
            value: k,
            label: v,
         }));
      }
      return [];
   }

   async fetchAttributtes(opts: {
      onSuccess?: () => void;
      onPlanError?: (e: string) => void;
   }): Promise<void> {
      await this.jobAttributtesState.run(() => JobApiService.jobCreateJob(null), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.jobAttributes.set(resp.response);
                  this.skillsVm.setOptions(resp.response.skillList);
                  if (resp.response.planList.length === 1) {
                     const plan = resp.response.planList[0];
                     this.form.controls['planId'].setValue(plan.id.toString());
                  }
               });
               opts.onSuccess?.();
            }
            if (resp.status === 500) {
               opts?.onPlanError?.(resp.message);
            }
         },
      });
   }

   async createJob(opts: {
      onSuccess?: (res: CreateJobResponse) => void;
      onError?: (e: ApiError) => void;
   }): Promise<void> {
      const jobAttributes = this.jobAttributes.get();
      this.form.validate();

      if (!this.form.valid || !jobAttributes) {
         return;
      }
      const dto: CreateJobDto = {
         user_plan_id: Number(this.form.controls['planId'].value),
         job_title: this.form.controls['job_title'].value,
         category: this.form.controls['category'].value,
         category_2: this.form.controls['category_1'].value,
         category_3: this.form.controls['category_2'].value,
         subCategory: this.form.controls['subCategory'].value,
         subCategory_2: this.form.controls['subCategory_1'].value,
         subCategory_3: this.form.controls['subCategory_2'].value,
         experience: this.form.controls['experience'].value,
         lng_1: this.form.controls['lng_1'].value,
         lng_2: this.form.controls['lng_2'].value,
         lng_skill_1: this.form.controls['lng_skill_1'].value,
         lng_skill_2: this.form.controls['lng_skill_2'].value,
         work_type: this.form.controls['work_type'].value,
         skill: this.form.controls['skill'].value,

         salary_max: this.form.controls['salary_max'].value,
         salary_min: this.form.controls['salary_min'].value,
         salary_currency: this.form.controls['salary_currency'].value,

         jobDescription: this.form.controls['jobDescription'].value,

         country: Number(this.form.controls['country'].value),
         city: Number(this.form.controls['city'].value),
         form_link: this.form.controls['form_link'].value,

         only_resume: this.form.controls['only_resume'].value,
         questionary: this.form.controls['questionary'].value,

         company_name: jobAttributes.jobData.company_name,
         logo: jobAttributes.jobData.company_logo,
         companyProfile: jobAttributes.jobData.companyProfile,
         company_website: jobAttributes.jobData.company_website,
      };

      await this.createJobState.run(() => JobApiService.jobCreateJob(dto), {
         onSuccess: opts.onSuccess,
         onError: opts.onError,
      });
   }
}
